/* eslint-disable */
import React from "react";
import {
  Card,
  Group,
  Text,
  createStyles,
  Grid,
  Box,
  ThemeIcon,
  Divider,
  Loader,
  Center,
} from "@mantine/core";
import {
  IconClockHour4,
  IconCalendarTime,
  IconSailboat,
  IconBrandWechat,
} from "@tabler/icons-react";

import { useGetDashboardStatistics } from "api/attendance/dashboardStatistics";
import type { DashboardStatistic } from "api/attendance/dashboardStatistics";
import { useGetUser } from "api/users/getUser";
import type { UseQueryResult } from "@tanstack/react-query";
import { useGetTotalHour } from "api/attendance/getUserWorkingHours";

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 8,
    padding: 18,
    border: `1px solid ${theme.colors.gray[3]}`,
    // background: theme.white,
    boxShadow: "0 6px 18px rgba(12,20,40,0.04)",
    // boxShadow: "0 6px 18px rgba(12, 20, 40, 0.04)",
    // border: "1px solid rgba(15, 23, 42, 0.04)",
    minHeight: 140,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: theme.white,
  },
  iconBox: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  valueRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    marginTop: 8,
  },
  numerator: {
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1,
    color: "#0f172a",
  },
  denominator: {
    fontSize: 16,
    fontWeight: 700,
    color: "rgba(15,23,42,0.45)",
  },
  label: {
    color: "rgba(15,23,42,0.6)",
    marginTop: 8,
    fontSize: 14,
  },
  trendRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
  },
  trendPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 13,
  },
  up: {
    background: "#ecfdf5",
    color: "#059669",
  },
  down: {
    background: "#fff1f2",
    color: "#dc2626",
  },
  smallLabel: {
    color: "rgba(15,23,42,0.5)",
    fontSize: 13,
  },
}));

interface Metric {
  title: string;
  valuePrimary: string;
  valueSecondary?: string;
  color: string;
  icon?: React.ReactNode;
  trendPercent?: string;
  trendDirection?: "up" | "down";
  trendLabel?: string;
}

interface Props {
  metrics?: Metric[];
}

export default function AttendanceMetricsCards({ metrics }: Props) {
  const { classes, cx } = useStyles();

  const { data: payload, isLoading: statsLoading } =
    useGetDashboardStatistics() as UseQueryResult<DashboardStatistic, unknown>;

  const { data: workingHours, isLoading, isError } = useGetTotalHour();

  const format = (n: number | undefined) =>
    n === undefined || Number.isNaN(Number(n)) ? "0.00" : Number(n).toFixed(2);

  const computedMetrics: Metric[] | null = React.useMemo(() => {
    if (!payload) return null;

    const expectedDaily = workingHours?.totalHours ?? 0;

    const todayVal = Number(payload.today?.totalWorkingHours ?? 0);
    const weekVal = Number(payload.thisWeek?.totalWorkingHours ?? 0);
    const monthVal = Number(payload.thisMonth?.totalWorkingHours ?? 0);
    const totalWeekVal = Number(payload.thisWeek?.totalWeekHours ?? 0);
    const totalmonthVal = Number(payload.thisMonth?.totalMonthlyHours ?? 0);
    const overtimeVal = Number(payload.overtime?.hours ?? 0);
    const overtimeMax = Number(payload.overtime?.maxHours ?? 0);

    const pctOf = (value: number, max: number) =>
      max > 0 ? Math.min(100, (value / max) * 100) : 0;

    const todayPct = pctOf(todayVal, expectedDaily);
    const weekPct = pctOf(weekVal, expectedDaily * 5);
    const monthPct = pctOf(monthVal, expectedDaily * 22);
    const overtimePct = pctOf(
      overtimeVal,
      overtimeMax || Math.max(1, overtimeVal)
    );
    const remainingVal = Number(payload.remaining?.hours ?? 0);
    const remainingPct = pctOf(remainingVal, expectedDaily);

    const makeMetric = (
      title: string,
      value: number,
      max: number,
      color: string,
      icon: React.ReactNode,
      label: string,
      pct: number
    ): Metric => ({
      title,
      valuePrimary: format(value),
      valueSecondary: format(max),
      color,
      icon,
      trendPercent: `${Math.round(pct)}%`,
      trendDirection: pct >= 50 ? "up" : "down",
      trendLabel: label,
    });

    return [
      makeMetric(
        "Total Hours Today",
        todayVal,
        expectedDaily,
        "#f97316",
        <IconClockHour4 size={18} color="white" />,
        "Today",
        todayPct
      ),
      makeMetric(
        "Total Hours Week",
        weekVal,
        totalWeekVal,
        "#111827",
        <IconCalendarTime size={18} color="white" />,
        "This Week",
        weekPct
      ),
      makeMetric(
        "Total Hours Month",
        monthVal,
        totalmonthVal,
        "#3b82f6",
        <IconSailboat size={18} color="white" />,
        "This Month",
        monthPct
      ),
      makeMetric(
        "Overtime this Month",
        overtimeVal,
        overtimeMax || Math.max(1, overtimeVal),
        "#ec4899",
        <IconBrandWechat size={18} color="white" />,
        "Overtime",
        overtimePct
      ),
    ];
  }, [payload, workingHours]);

  const demo: Metric[] = [
    {
      title: "Total Hours Today",
      valuePrimary: "8.36",
      valueSecondary: "9",
      color: "#f97316",
      icon: <IconClockHour4 size={18} color="white" />,
      trendPercent: "5% ",
      trendDirection: "up",
      trendLabel: "This Week",
    },
    {
      title: "Total Hours Week",
      valuePrimary: "10",
      valueSecondary: "40",
      color: "#111827",
      icon: <IconCalendarTime size={18} color="white" />,
      trendPercent: "7% ",
      trendDirection: "up",
      trendLabel: "Last Week",
    },
    {
      title: "Total Hours Month",
      valuePrimary: "75",
      valueSecondary: "98",
      color: "#3b82f6",
      icon: <IconSailboat size={18} color="white" />,
      trendPercent: "8% ",
      trendDirection: "down",
      trendLabel: "Last Month",
    },
    {
      title: "Overtime this Month",
      valuePrimary: "16",
      valueSecondary: "28",
      color: "#ec4899",
      icon: <IconBrandWechat size={18} color="white" />,
      trendPercent: "6% ",
      trendDirection: "down",
      trendLabel: "Last Month",
    },
  ];

  if (statsLoading || isLoading) {
    return (
      <Center style={{ height: 140 }}>
        <Loader />
      </Center>
    );
  }

  const list =
    metrics && metrics.length === 4 ? metrics : computedMetrics ?? demo;

  return (
    <Grid>
      {list.map((m, i) => (
        <Grid.Col xs={12} sm={6} md={3} key={`${m.title}-${i}`}>
          <Card className={classes.card} radius="md">
            <div>
              <Group position="apart" align="flex-start">
                <ThemeIcon
                  size={36}
                  radius={8}
                  style={{ background: m.color }}
                  className={classes.iconBox}
                >
                  {m.icon}
                </ThemeIcon>
              </Group>

              <div className={classes.valueRow}>
                <div className={classes.numerator}>{m.valuePrimary}</div>
                {m.valueSecondary && (
                  <div className={classes.denominator}>
                    / {m.valueSecondary}
                  </div>
                )}
              </div>

              <div className={classes.label}>{m.title}</div>
            </div>

            <div>
              <Divider my="sm" />
              <div className={classes.trendRow}>
                <Box
                  className={cx(
                    classes.trendPill,
                    m.trendDirection === "up" ? classes.up : classes.down
                  )}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {m.trendDirection === "up" ? (
                      <path
                        d="M12 19V6"
                        stroke="#059669"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ) : (
                      <path
                        d="M12 5v13"
                        stroke={
                          m.trendDirection === "down" ? "#dc2626" : "#059669"
                        }
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                    {m.trendDirection === "up" ? (
                      <path
                        d="M5 12l7-7 7 7"
                        stroke="#059669"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    ) : (
                      <path
                        d="M19 12l-7 7-7-7"
                        stroke="#dc2626"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    )}
                  </svg>

                  <Text size="sm" weight={700} style={{ marginLeft: 6 }}>
                    {m.trendPercent}
                  </Text>
                </Box>

                <Text className={classes.smallLabel}>{m.trendLabel}</Text>
              </div>
            </div>
          </Card>
        </Grid.Col>
      ))}
    </Grid>
  );
}
